import React, { useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../../lib/supabase";
import { useRBAC } from "../../../hooks/useRBAC";

interface BulkDismissProps {
  ids: string[];
  /**
   * The ghl_user_id of the setter whose proof grid is currently being viewed.
   * Used to decide which RBAC flag applies:
   *   • viewedSetterId === currentUserGhlId → use canDismissOwnConversations
   *   • else                                → use canDismissAnyConversation
   * null/undefined means org-wide view (no setter scoping) — falls through to "any".
   */
  viewedSetterId?: string | null;
  /** Caller's GHL user id, from profiles.ghl_user_id, used for own-vs-any branch. */
  currentUserGhlId?: string | null;
  /** Invoked with the just-dismissed ids so parent can update local state + invalidate cache. */
  onAfterDismiss: (ids: string[], dismissType: "dismissed" | "dead") => void;
}

type DismissType = "dismissed" | "dead";

/**
 * S29 (May 2026) — bulk Dismiss/Kill button pair for ProofGridView action bar.
 *
 * Dismiss: soft hide; conv reappears if lead messages again (time-aware filter).
 * Kill: permanent; conv never re-surfaces regardless of new messages.
 *
 * Server-side RPC enforces RBAC + cross-org reject; this component is a UX gate
 * on top so disallowed roles don't see the buttons at all.
 *
 * Kill flow shows a portal-ed confirm modal (uses detail-overlay, not liquid-glass
 * — overlays trigger a ::before z-index trap on liquid-glass per /css doctrine).
 */
export const BulkDismiss: React.FC<BulkDismissProps> = ({
  ids,
  viewedSetterId,
  currentUserGhlId,
  onAfterDismiss,
}) => {
  const { canDismissOwnConversations, canDismissAnyConversation } = useRBAC();

  // Decide which flag applies based on whose grid the viewer is looking at.
  const viewingOwnGrid =
    viewedSetterId !== undefined &&
    viewedSetterId !== null &&
    currentUserGhlId !== undefined &&
    currentUserGhlId !== null &&
    viewedSetterId === currentUserGhlId;

  const canAct = viewingOwnGrid ? canDismissOwnConversations : canDismissAnyConversation;

  const [busy, setBusy] = useState<DismissType | null>(null);
  const [showKillConfirm, setShowKillConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canAct) return null;

  const disabled = ids.length === 0 || busy !== null;

  const runDismiss = async (dismissType: DismissType) => {
    if (ids.length === 0) return;
    setBusy(dismissType);
    setError(null);
    try {
      if (!supabase) {
        setError("Database client not initialized");
        return;
      }
      const { error: rpcError } = await supabase.rpc("bulk_dismiss_conversations", {
        p_conversation_ids: ids,
        p_dismiss_type: dismissType,
      });
      if (rpcError) {
        setError(rpcError.message);
        return;
      }
      onAfterDismiss(ids, dismissType);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bulk dismiss failed");
    } finally {
      setBusy(null);
    }
  };

  const handleKillClick = () => setShowKillConfirm(true);
  const handleKillConfirm = async () => {
    setShowKillConfirm(false);
    await runDismiss("dead");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => runDismiss("dismissed")}
        disabled={disabled}
        data-testid="bulk-dismiss-button"
        className="liquid-glass-light px-3 py-1 token-11 text-ds-amber hover:text-glass disabled:opacity-50 disabled:cursor-not-allowed"
        title={ids.length === 0 ? "Select conversations first" : undefined}
      >
        {busy === "dismissed" ? "Dismissing…" : `Dismiss (${ids.length})`}
      </button>
      <button
        type="button"
        onClick={handleKillClick}
        disabled={disabled}
        data-testid="bulk-kill-button"
        className="liquid-glass-light px-3 py-1 token-11 text-ds-red hover:text-glass disabled:opacity-50 disabled:cursor-not-allowed"
        title={ids.length === 0 ? "Select conversations first" : undefined}
      >
        {busy === "dead" ? "Killing…" : `Kill (${ids.length})`}
      </button>
      {error && (
        <span className="token-11 text-ds-red" data-testid="bulk-dismiss-error" role="alert">
          {error}
        </span>
      )}
      {showKillConfirm &&
        createPortal(
          <div
            // z-[9999] sits ONE tier above SetterProofModal's z-[9998]. createPortal alone
            // doesn't beat z-index — both modals portal to document.body so the only thing
            // deciding paint order is the integer. /css gotcha: nested modal stacks need
            // ascending tiers per depth (or a global modal manager — overkill for one nesting).
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
            data-testid="bulk-kill-confirm-modal"
            onClick={() => setShowKillConfirm(false)}
          >
            <div className="detail-overlay max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="token-16 font-semibold text-glass mb-2">
                Permanently remove {ids.length}{" "}
                {ids.length === 1 ? "conversation" : "conversations"}?
              </h3>
              <p className="token-13 text-glass-secondary mb-5">
                Killed conversations won&apos;t reappear in DM Analytics even if the lead messages
                again. Use Dismiss if you want them back when the lead replies.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowKillConfirm(false)}
                  data-testid="bulk-kill-confirm-cancel"
                  className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleKillConfirm}
                  data-testid="bulk-kill-confirm-confirm"
                  className="liquid-glass-light px-3 py-1 token-11 text-ds-red hover:text-glass"
                >
                  Kill {ids.length}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
